import { Box, ThemeProvider, useTheme } from '@mui/material';
import clsx from 'clsx';
import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useDetectSticky } from '../../../hooks/useDetectSticky';
import { createTheme } from '../../../themes/sde/dark';
import type { Theme } from '../../../themes/themes';
import { Container } from '../../Container/Container';
import { Divider } from '../../Divider/Divider';
import { STICKY_HEADER_BAR_DATA_ATTRIBUTE } from '../../HeaderBar/HeaderBar.constants';
import { Typography } from '../../Typography/Typography';
import { Link, useAnchorList } from '../hooks';
import { Navigation } from './Navigation';
import { getStyles } from './Opener.styles';

const darkTheme = createTheme();

export enum OpenerVariant {
  WHITE = 'white',
  TRANSPARENT = 'transparent',
}

export interface OpenerProps {
  title: string;
  links: Link[];
  variant: 'transparent' | 'white';
  onLinkClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const Opener: React.FC<OpenerProps> = ({
  title,
  links = [],
  variant = OpenerVariant.TRANSPARENT,
  onLinkClick: onLinkClickProp,
}) => {
  const theme = useTheme<Theme>();
  const styles = getStyles({ theme });
  const [showNavigation, setShowNavigation] = useState(false);
  const navigationBarStyles = Object.assign({}, styles.navigationBar, showNavigation && styles.navigationBarShow);
  const navigationBarStickyHeaderStyles = Object.assign(
    {},
    styles.navigationStickyHeaderBar,
    showNavigation && styles.navigationStickyHeaderBarShow
  );
  const navigationRef = useRef(null);
  const navigationBarRef = useRef(null);
  const { currentAnchor, scrollTo } = useAnchorList(links, navigationBarRef);
  const { isSticky } = useDetectSticky({ id: STICKY_HEADER_BAR_DATA_ATTRIBUTE });

  useEffect(() => {
    const handleScroll = () => {
      if (navigationRef?.current) {
        const bottom = navigationRef.current.getBoundingClientRect().top;
        const footer =
          document.getElementsByTagName('footer')[0]?.getBoundingClientRect().top - parseInt(theme.spacing(10));
        if (footer) {
          setShowNavigation(bottom - parseInt(theme.spacing(4)) < 0 && footer > 0);
        } else {
          setShowNavigation(bottom - parseInt(theme.spacing(4)) < 0);
        }
      }
    };

    const handleResize = () => {
      handleScroll();
    };

    document.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onLinkClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, anchor: string) => {
      event.preventDefault();
      onLinkClickProp?.(event);
      scrollTo(anchor);
    },
    [onLinkClickProp, scrollTo]
  );
  return (
    <>
      <Box sx={styles.container[variant]} className={clsx('InPageNaviBarPrimary-Opener')}>
        <Divider sx={styles.divider[variant]} />
        <Typography
          variant="body1"
          fontWeight="bold"
          component="h2"
          sx={styles.title[variant]}
          className={clsx('InPageNaviTitle-Opener')}
        >
          {title}
        </Typography>
        <Box ref={navigationRef} sx={styles.navigation[variant]}>
          <Navigation active={currentAnchor} links={links} onLinkClicked={onLinkClick} variant={variant} />
        </Box>
      </Box>
      <ThemeProvider theme={darkTheme}>
        <Box
          sx={isSticky ? navigationBarStickyHeaderStyles : navigationBarStyles}
          ref={navigationBarRef}
          role="navigation"
          aria-label={title}
          className={clsx('InPageNaviBarSecondary-Opener')}
        >
          <Container disableGutters>
            <Box sx={styles.navigationBarInner}>
              <Navigation active={currentAnchor} links={links} onLinkClicked={onLinkClick} variant={variant} />
            </Box>
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
};
